import { Form, Row, Col, Select, Button, Empty } from "antd";
import React, { useEffect, useState } from "react";
import { PlusCircleOutlined, CloseOutlined } from "@ant-design/icons";
import {
  CommonButton,
  CommonInput,
  CommonSelect,
  CommonTextMedium,
} from "../../../../../components/common/AtomCommonComponents";
import {
  CommonFormItemWithLabel,
  CommonModal,
} from "../../../../../components/common/MoleculesCommonComponent";
//service

import DialogUtils from "../../../../../utils/DialogUtils";
import ValidationUtils from "../../../../../utils/ValidationUtils";
import TextFormatUtils from "../../../../../utils/TextFormatUtils";
import HomeDeliveryService from "../../../../../services/api/HomeDeliveryService";

interface AddEditBoxProductsModalProps {
  isAddEditBoxProductsModalVisible: boolean;
  onCompleteModal: Function;
  onCancelModal: () => void;
  editData: any;
  productsData: any;
  units: any;
}

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

const AddEditBoxProductsModal: React.FC<AddEditBoxProductsModalProps> = (
  props
) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<string>("add");
  // const [productsList, setProductsList] = useState([]);
  const [products, setProducts] = useState();

  const homeDeliveryService = new HomeDeliveryService();

  useEffect(() => {
    const initialize = () => {
      if (props.isAddEditBoxProductsModalVisible) {
        if (props.editData) {
          setMode("edit");

          const rawData = deepClone(props.editData);

          const overrides =
            rawData.original_product_id || rawData.new_product_id;

          if (overrides) {
            const updatedValues: any = {
              product_id:
                rawData.new_product_id ??
                rawData.original_product_id ?? rawData.product_id,
              product_name:
                rawData.new_product_name ??
                rawData.original_product_name ?? rawData.product_name,
              name: rawData.name ?? " ",
              quantity:
                rawData.new_product_quantity ??
                rawData.original_product_quantity ?? rawData.quantity,
              unit:
  rawData.new_product_unit ??
  rawData.original_product_unit ??
  (rawData.unit === 0 ? undefined : rawData.unit)
            };

            Object.keys(rawData).forEach((key) => {
              if (key !== "index") {
                delete rawData[key];
              }
            });
            Object.assign(rawData, updatedValues);

            form.setFieldsValue(rawData);
          } else {
            form.setFieldsValue(props.editData);
          }
        } else {
          setMode("add");
          form.resetFields();
        }
      }
    };
    initialize();
  }, [props.isAddEditBoxProductsModalVisible, form, props.editData]);

  useEffect(() => {
    fetchProducts();
  }, [props.isAddEditBoxProductsModalVisible]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (mode === "add") {
        props.onCompleteModal(values, -1);
      } else if (mode === "edit") {
        props.onCompleteModal(values, props.editData.index);
      } else {
        DialogUtils.error("Something went wrong!");
      }
    } catch (error) {
      return;
    }
  };

  const fetchProducts = async () => {
    try {
      const result = await homeDeliveryService.getProductNames("");
      const products = result.map(
        ({ id, value }: { id: string; value: string }) => ({
          value: id,
          label: value,
        })
      );
      setProducts(products);
    } catch (error) {
      DialogUtils.error("Failed to fetch products.");
    }
  };

  return (
    <CommonModal
      maskClosable={false}
      title={mode === "edit" ? "Edit Products" : "Add Products"}
      visible={props.isAddEditBoxProductsModalVisible}
      onCancel={props.onCancelModal}
      style={{ transform: "translateX(-1.75%)" }}
      footer={[
        <CommonButton
          key="cancel"
          onClick={() => {
            props.onCancelModal();
          }}
        >
          Cancel
        </CommonButton>,
        <CommonButton
          key="save"
          color="white"
          background="primary"
          onClick={onSubmit}
        >
          Save
        </CommonButton>,
      ]}
    >
      <Form form={form} validateTrigger="onChange" onFinish={onSubmit}>
        <div id="select-three">
          <CommonFormItemWithLabel
            label="Products"
            name="product_id"
            rules={[{ required: true, message: "Product is required." }]}
          >
            <CommonSelect
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              notFoundContent={
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No data found."
                />
              }
              showArrow
              placeholder="Select Products"
              options={products}
              showSearch
              color="primary"
              noIndent={true}
              filterOption={(input: any, option: any) =>
                (option!.label?.toLowerCase() as unknown as string)?.includes(
                  input?.toLowerCase()
                )
              }
            />
          </CommonFormItemWithLabel>
        </div>

        <CommonFormItemWithLabel
          label={"Quantity"}
          name="quantity"
          rules={[
            { required: true, message: "Quantity is required." },
            {
              validator: (rule, value) => {
                const result = ValidationUtils.validateAndReplaceNumber(
                  value,
                  form,
                  "quantity",
                  "quantity"
                );

                // Only validate > 0 if there is an entered (non-empty) value
                if (value !== null && value !== undefined && value !== "") {
                  const num = Number(value);
                  if (!isNaN(num) && num <= 0) {
                    return Promise.reject("Quantity must be greater than 0.");
                  }
                }

                return result;
              },
            },
          ]}
        >
          <CommonInput
            maxLength={10}
            placeholder="Input Quantity"
            onInput={(e: any) => {
              TextFormatUtils.upperText(e);
            }}
            color="primary"
          />
        </CommonFormItemWithLabel>
        <div id="select-six">
          <CommonFormItemWithLabel
            label="Unit"
            name="unit"
            rules={[{ required: true, message: "Unit is required." }]}
          >
            <CommonSelect
              showArrow
              placeholder="Select Unit"
              color="primary"
              options={
                props.units
                  ? props.units.map((item: any) => ({
                      value: item.name,
                      label: item.name,
                    }))
                  : []
              }
              filterOption={(input: any, option: any) =>
                (option!.label?.toLowerCase() as unknown as string)?.includes(
                  input?.toLowerCase()
                )
              }
              notFoundContent={
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No units found."
                />
              }
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
            />
          </CommonFormItemWithLabel>
        </div>
      </Form>
    </CommonModal>
  );
};

export default AddEditBoxProductsModal;
